import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const PwaInstallContext = createContext(null);

const DISMISS_KEY = "eqr_pwa_install_dismissed";

function isStandalone() {
  if (typeof window === "undefined") return true;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function PwaInstallProvider({ children }) {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [standalone, setStandalone] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(() => readDismissed());

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    const onInstalled = () => {
      setDeferred(null);
      setStandalone(true);
      setOpen(false);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const shouldOffer = useMemo(() => !standalone && !dismissed, [standalone, dismissed]);

  const promptAfterSetup = useCallback(() => {
    if (isStandalone()) {
      setStandalone(true);
      return;
    }
    if (readDismissed()) {
      setDismissed(true);
      return;
    }
    setOpen(true);
  }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return { outcome: "unavailable" };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice?.outcome === "accepted") {
      setStandalone(true);
      setOpen(false);
    } else {
      dismiss();
    }
    return choice;
  }, [deferred, dismiss]);

  const value = useMemo(
    () => ({
      open,
      promptAfterSetup,
      dismiss,
      install,
      canNativeInstall: Boolean(deferred),
      isIos: isIos(),
      isStandalone: standalone,
      shouldOffer,
    }),
    [open, promptAfterSetup, dismiss, install, deferred, standalone, shouldOffer],
  );

  return <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>;
}

export function usePwaInstall() {
  const ctx = useContext(PwaInstallContext);
  if (!ctx) {
    return {
      open: false,
      promptAfterSetup: () => {},
      dismiss: () => {},
      install: async () => ({ outcome: "unavailable" }),
      canNativeInstall: false,
      isIos: false,
      isStandalone: true,
      shouldOffer: false,
    };
  }
  return ctx;
}
